/**
 * Full-page chat at /chat — the roomy two-pane container for the shared ChatDockInner
 * (conversation list on the left, active thread + composer on the right). Same internals
 * as the pop-out drawer, just a bigger container.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import ChatDockInner from '../components/ChatDock';
import InfoModal from '../components/InfoModal';

const Chat: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Back arrow: return to wherever the user came from (People lane, navbar link, etc.).
  // Fall back to home if /chat was opened directly with no in-app history to pop.
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="container" style={{ maxWidth: 1400, margin: '0 auto', padding: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={goBack}
            aria-label="Back"
            title="Back"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              flex: '0 0 auto',
              borderRadius: 8,
              border: `1px solid ${theme.border}`,
              backgroundColor: theme.surface,
              color: theme.text,
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
            }}
          >
            ←
          </button>
          <h1 style={{ color: theme.text, margin: 0 }}>Chat</h1>
          <InfoModal
            title="Chat"
            body={[
              "Start a chat with one AI rep (direct) or several (a group), optionally scoped to an initiative. You always post as your global identity (set in the navbar).",
              "AI messages are clearly labelled \"· AI\" in purple — never mistaken for the real person. On any AI message use \"··· actions\" to Correct & Teach (saves a lesson to that rep's memory), Endorse, or Bring in the real person.",
              "Filter the list by initiative to see all of an initiative's chats over time. \"Bring in\" pins a \"Waiting on X\" chip until that person replies.",
              "Pick a Quest in an open chat's header to scope it to that quest. Every rep in the chat then knows the quest's outcome and team, and emails the quest's own people through its mailer, so you never have to paste a quest id.",
            ]}
          />
        </div>
        <p style={{ color: theme.textSecondary, margin: '4px 0 0', fontSize: 14 }}>
          Talk 1:1 with a team member's AI rep, or open a group room. In a group, type{' '}
          <strong>@rep</strong> to target one. Scope a chat to an initiative to keep its context, and
          pick a Quest in the chat header so its reps mail that quest's people.
        </p>
      </div>
      <div
        style={{
          height: 'calc(100vh - 180px)',
          minHeight: 420,
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          overflow: 'hidden',
          backgroundColor: theme.background,
        }}
      >
        <ChatDockInner variant="page" />
      </div>
    </div>
  );
};

export default Chat;
